/**
 * The pause menu's pure model (spec §10, plan G12), separate from the DOM so it is unit-tested in Node.
 *
 * Screens (D-617, spec §4): `main` (the 8 system rows: Resume, Dashboard, Save & Load, Settings, Audio, Controls,
 * Help, Quit to title; Dashboard opens the real dashboard, or reads *Close dashboard* when the menu is open over it,
 * W3 card M4), `saves`
 * (slot rows with Save / Load / Export actions, set by the feature + Back), `help` (pages + Back) and `helpPage`
 * (one page; up/down scroll), `quit` (confirm + Cancel), `settings` (controls feel, graphics and screen shake + Back),
 * Settings also holds the Interface group (spec 2026-10-09 §5.6: UI scale, text contrast, reduced motion, HUD labels,
 * dashboard time, hold to confirm; card F2). `audio` (master and bus volumes, hull effect, night mode, mono + Back; audio-direction §6) and `controls` (the
 * reference generated from bindings.json + Back). Inputs come from the keyboard, the D-pad / left stick
 * and the mouse, all reduced to `MenuInput`s: up/down move the focus (wrapping), left/right adjust the focused
 * setting, activate presses it, back returns to the main screen (restoring the focus that left it), and
 * `start` resumes from anywhere. Setting changes mutate the `Settings` object passed in and report which key
 * changed; the feature persists and applies them.
 *
 * Also here: the gamepad navigation reader (the controls reference lives in `controlsRef.ts`)
 * (`stepPadNav`: rising edges plus held-direction auto-repeat, allocation-free, run every frame).
 */
import { DASHBOARD_TIME_MODES, REDUCED_MOTION_MODES, TEXT_CONTRASTS, type Settings } from '../app/feature'
import { AIM_ASSIST_LEVELS, type AimAssistLevel } from '../data/schemas'
import type { PadLike } from '../input/playerInput'
import { QUALITY_PRESETS, type QualityPreset } from '../render/contracts'
import type { MenuNavConfig, SettingRange, SettingsConfig } from '../settings/settings'
import menuText from './menu.json'
import { HUD_LABEL_MODES } from './reach/config'

export type MenuScreen = 'main' | 'saves' | 'settings' | 'audio' | 'controls' | 'help' | 'helpPage' | 'quit'
export type MenuInput = 'up' | 'down' | 'left' | 'right' | 'activate' | 'back' | 'start'
/**
 * What an input did: nothing, moved the focus, switched screens, changed a setting, scrolled, asks to resume, or
 * (`dashboard`) asks the feature to open the dashboard, or close it when the menu is open over it, and resume.
 */
export type MenuAction = 'none' | 'moved' | 'screen' | 'changed' | 'scrollUp' | 'scrollDown' | 'resume' | 'request' | 'denied' | 'dashboard'

/** What the feature must do after a `request` action (async work the pure model can't). */
export type MenuRequest = { readonly kind: SlotActionId; readonly slot: string } | { readonly kind: 'quit' }

export type MainButtonId = 'resume' | 'openDashboard' | 'openSaves' | 'openSettings' | 'openAudio' | 'openControls' | 'openHelp' | 'quitTitle'
export type ButtonId = MainButtonId | 'back' | 'quitConfirm' | HelpButtonId
/** A help page's button on the help screen. */
export type HelpButtonId = `help:${string}`
export type SliderId = 'uiScale' | 'mouseSensitivity' | 'masterVolume' | 'musicVolume' | 'sfxVolume' | 'uiVolume' | 'commsVolume' | 'hullEffect'
export type ToggleId = 'invertY' | 'defaultFA' | 'screenShake' | 'nightMode' | 'mono' | 'holdToConfirm'
/** Settings shown as a segmented select (one option per value). */
export type SelectId = 'quality' | 'aimAssist' | 'textContrast' | 'reducedMotion' | 'hudLabels' | 'dashboardTime'

export type SlotActionId = 'save' | 'load' | 'export'
export interface SlotAction {
  readonly id: SlotActionId
  readonly label: string
  readonly enabled: boolean
  /** Why a disabled action is disabled (shown when pressed). */
  readonly reason: string | null
}
/** A save slot row (spec §4.1): left/right pick an action, activate requests it. */
export interface SlotItem {
  readonly kind: 'slot'
  readonly id: `slot:${string}`
  readonly label: string
  readonly hint: string
  readonly actions: readonly SlotAction[]
}
/** A help page's id and title (`features/menu/help.json`). */
export interface HelpPageRef {
  readonly id: string
  readonly title: string
  readonly hint: string
}

export type MenuItem =
  | { readonly kind: 'button'; readonly id: ButtonId; readonly label: string; readonly hint: string; readonly group?: string }
  | SlotItem
  | { readonly kind: 'slider'; readonly id: SliderId; readonly label: string; readonly hint: string; readonly range: SettingRange; readonly group?: string }
  | { readonly kind: 'toggle'; readonly id: ToggleId; readonly label: string; readonly hint: string; readonly group?: string }
  | { readonly kind: 'select'; readonly id: SelectId; readonly label: string; readonly hint: string; readonly options: readonly string[]; readonly group?: string }

export interface MenuModel {
  open: boolean
  screen: MenuScreen
  focus: number
  /** Focus on the main screen to restore when leaving a sub-screen. */
  mainFocus: number
  /** The menu is open over the dashboard: the Dashboard row reads *Close dashboard* (spec §4). */
  dashboardUnder: boolean
  /** The setting the last `changed` action touched. */
  changed: keyof Settings | null
  /** The last `request` action's work. */
  request: MenuRequest | null
  /** The last `denied` action's reason. */
  denied: string | null
  /** The focused slot row's chosen action (index into its `actions`). */
  slotAction: number
  /** The open help page id (`helpPage` screen). */
  helpPage: string
  /** Focus on the help list to restore when leaving a page. */
  helpFocus: number
  /** The save slot rows (`setSaveSlots`); the saves screen is these + Back. */
  saveSlots: readonly MenuItem[]
  readonly items: Readonly<Record<MenuScreen, readonly MenuItem[]>>
}

const T = menuText

export const QUALITY_LABELS: Readonly<Record<QualityPreset, string>> = { low: 'Low', medium: 'Medium', high: 'High' }
export const AIM_ASSIST_LABELS: Readonly<Record<AimAssistLevel, string>> = { off: 'Off', low: 'Low', standard: 'Standard', high: 'High' }

const IT = T.interface

/** Display labels of every select's options (the Interface ones from `menu.json`). */
const SELECT_LABELS: Readonly<Record<SelectId, Readonly<Record<string, string>>>> = {
  quality: QUALITY_LABELS,
  aimAssist: AIM_ASSIST_LABELS,
  textContrast: IT.options.textContrast,
  reducedMotion: IT.options.reducedMotion,
  hudLabels: IT.options.hudLabels,
  dashboardTime: IT.options.dashboardTime,
}

/** A select option's display label. */
export function selectOptionLabel(id: SelectId, value: string): string {
  return SELECT_LABELS[id][value] ?? value
}

/** The current value of a select's setting. */
export function selectValue(id: SelectId, settings: Settings): string {
  return settings[id]
}

/** The Interface group's rows (spec 2026-10-09 §5.6), labelled from `menu.json`. */
function interfaceItems(ranges: SettingsConfig['ranges']): MenuItem[] {
  const g = IT.group
  const r = IT.rows
  return [
    { kind: 'slider', id: 'uiScale', label: r.uiScale.label, hint: r.uiScale.hint, range: ranges.uiScale, group: g },
    { kind: 'select', id: 'textContrast', label: r.textContrast.label, hint: r.textContrast.hint, options: TEXT_CONTRASTS, group: g },
    { kind: 'select', id: 'reducedMotion', label: r.reducedMotion.label, hint: r.reducedMotion.hint, options: REDUCED_MOTION_MODES, group: g },
    { kind: 'select', id: 'hudLabels', label: r.hudLabels.label, hint: r.hudLabels.hint, options: HUD_LABEL_MODES, group: g },
    { kind: 'select', id: 'dashboardTime', label: r.dashboardTime.label, hint: r.dashboardTime.hint, options: DASHBOARD_TIME_MODES, group: g },
    { kind: 'toggle', id: 'holdToConfirm', label: r.holdToConfirm.label, hint: r.holdToConfirm.hint, group: g },
  ]
}

export const SCREEN_TITLES: Readonly<Record<MenuScreen, string>> = T.titles

const MAIN_IDS: readonly MainButtonId[] = ['resume', 'openDashboard', 'openSaves', 'openSettings', 'openAudio', 'openControls', 'openHelp', 'quitTitle']

function isMainId(id: string): id is MainButtonId {
  return (MAIN_IDS as readonly string[]).includes(id)
}

/** The main screen's 8 system rows (spec §4), in `menu.json` order. */
function mainItems(): MenuItem[] {
  return T.main.map((row) => {
    if (!isMainId(row.id)) throw new Error(`menu.json: unknown main row ${row.id}`)
    return { kind: 'button', id: row.id, label: row.label, hint: row.hint }
  })
}

export function createMenuModel(ranges: SettingsConfig['ranges'], helpPages: readonly HelpPageRef[] = []): MenuModel {
  const back: MenuItem = { kind: 'button', id: 'back', label: T.back.label, hint: T.back.hint }
  return {
    open: false,
    screen: 'main',
    focus: 0,
    mainFocus: 0,
    dashboardUnder: false,
    changed: null,
    request: null,
    denied: null,
    slotAction: 0,
    helpPage: '',
    helpFocus: 0,
    saveSlots: [],
    items: {
      main: mainItems(),
      saves: [back],
      help: [...helpPages.map((p): MenuItem => ({ kind: 'button', id: `help:${p.id}`, label: p.title, hint: p.hint })), back],
      helpPage: [{ kind: 'button', id: 'back', label: T.helpBack.label, hint: T.helpBack.hint }],
      quit: [
        { kind: 'button', id: 'quitConfirm', label: T.quitConfirm.label, hint: T.quitConfirm.hint },
        { kind: 'button', id: 'back', label: T.cancel.label, hint: T.cancel.hint },
      ],
      settings: [
        { kind: 'slider', id: 'mouseSensitivity', label: 'Mouse sensitivity', hint: 'How far the virtual stick moves per mouse count', range: ranges.mouseSensitivity },
        { kind: 'toggle', id: 'invertY', label: 'Invert Y', hint: 'Mouse and right stick: push forward to pitch up' },
        { kind: 'toggle', id: 'defaultFA', label: 'Flight assist at spawn', hint: 'Flight assist state every time you (re)spawn' },
        { kind: 'select', id: 'quality', label: 'Graphics quality', hint: 'Resolution scale, bloom, asteroid count, particles and detail distance', options: QUALITY_PRESETS },
        { kind: 'toggle', id: 'screenShake', label: 'Screen shake', hint: 'Camera shake when your ship is hit' },
        { kind: 'select', id: 'aimAssist', label: 'Aim assist', hint: 'How far your guns turn toward the lead point when it is near the crosshair', options: AIM_ASSIST_LEVELS },
        ...interfaceItems(ranges),
        back,
      ],
      audio: [
        { kind: 'slider', id: 'masterVolume', label: 'Master volume', hint: 'Every game sound', range: ranges.masterVolume },
        { kind: 'slider', id: 'musicVolume', label: 'Music', hint: 'The score', range: ranges.musicVolume },
        { kind: 'slider', id: 'sfxVolume', label: 'Effects', hint: 'Weapons, impacts, engines and explosions', range: ranges.sfxVolume },
        { kind: 'slider', id: 'uiVolume', label: 'Interface', hint: 'Cockpit tones, alarms and menu sounds', range: ranges.uiVolume },
        { kind: 'slider', id: 'commsVolume', label: 'Comms', hint: 'Radio chatter', range: ranges.commsVolume },
        { kind: 'slider', id: 'hullEffect', label: 'Hull effect', hint: 'How much of your own ship you hear through the hull', range: ranges.hullEffect },
        { kind: 'toggle', id: 'nightMode', label: 'Night mode', hint: 'Compress loud and quiet sounds for late-night play' },
        { kind: 'toggle', id: 'mono', label: 'Mono', hint: 'Play everything through both speakers alike' },
        back,
      ],
      controls: [back],
    },
  }
}

// ---------------------------------------------------------------------------------------------
// Cover: a modal screen opened over the pause menu (the away report, the Registrant) takes its inputs
// ---------------------------------------------------------------------------------------------

let covered = false
const coverListeners = new Set<(active: boolean) => void>()

/** While a modal screen is open over the menu, the menu is hidden and ignores its inputs (that screen navigates itself). */
export function setMenuCover(active: boolean): void {
  if (covered === active) return
  covered = active
  for (const fn of coverListeners) fn(active)
}

/** Calls `fn` when a screen starts or stops covering the menu (the view hides the menu under it). */
export function onMenuCover(fn: (active: boolean) => void): () => void {
  coverListeners.add(fn)
  return () => {
    coverListeners.delete(fn)
  }
}

/** True while a modal screen over the menu has the menu's inputs. */
export function menuCovered(): boolean {
  return covered
}

/** The main screen with row 2 relabelled *Close dashboard* (the menu is open over the dashboard, spec §4). */
const withClose = new WeakMap<readonly MenuItem[], readonly MenuItem[]>()

function mainOverDashboard(base: readonly MenuItem[]): readonly MenuItem[] {
  const cached = withClose.get(base)
  if (cached !== undefined) return cached
  const items = Object.freeze(base.map((i): MenuItem => (i.id === 'openDashboard' ? { ...i, label: T.closeDashboard.label, hint: T.closeDashboard.hint } : i)))
  withClose.set(base, items)
  return items
}

const withSlots = new WeakMap<readonly MenuItem[], readonly MenuItem[]>()

export function currentItems(m: MenuModel): readonly MenuItem[] {
  const base = m.items[m.screen]
  if (m.screen === 'saves') {
    const cachedSlots = withSlots.get(m.saveSlots)
    if (cachedSlots !== undefined) return cachedSlots
    const slots = Object.freeze([...m.saveSlots, ...base])
    withSlots.set(m.saveSlots, slots)
    return slots
  }
  return m.screen === 'main' && m.dashboardUnder ? mainOverDashboard(base) : base
}

/** Replaces the save slot rows (the feature builds them from the career's slot list); keeps the focus in range. */
export function setSaveSlots(m: MenuModel, slots: readonly MenuItem[]): void {
  m.saveSlots = slots
  if (m.screen !== 'saves') return
  const items = currentItems(m)
  if (m.focus >= items.length) m.focus = items.length - 1
  const item = items[m.focus]
  if (item.kind !== 'slot' || m.slotAction >= item.actions.length) m.slotAction = 0
}

export function focusedItem(m: MenuModel): MenuItem {
  return currentItems(m)[m.focus]
}

/** Opens the menu on its main screen; `dashboardUnder` when it opens over the dashboard (Esc at its top, P). */
export function openMenu(m: MenuModel, dashboardUnder = false): void {
  m.open = true
  m.dashboardUnder = dashboardUnder
  m.screen = 'main'
  m.focus = 0
  m.mainFocus = 0
  m.changed = null
  m.request = null
  m.denied = null
  m.slotAction = 0
}

export function closeMenu(m: MenuModel): void {
  m.open = false
}

/** Mouse hover or click: focuses item `index` of the current screen. Returns whether the focus moved. */
export function focusItem(m: MenuModel, index: number): boolean {
  if (index < 0 || index >= currentItems(m).length || index === m.focus) return false
  m.focus = index
  m.slotAction = 0
  return true
}

/** Mouse click on a slot row's action button: focuses the row and picks the action. */
export function focusSlotAction(m: MenuModel, index: number, action: number): void {
  focusItem(m, index)
  const item = currentItems(m)[m.focus]
  if (item.kind === 'slot' && action >= 0 && action < item.actions.length) m.slotAction = action
}

function roundToStep(value: number, range: SettingRange): number {
  const snapped = range.min + Math.round((value - range.min) / range.step) * range.step
  const clamped = Math.min(range.max, Math.max(range.min, snapped))
  // Kill float noise (0.1 + 0.2) so stored values stay tidy.
  return Math.round(clamped * 1e6) / 1e6
}

/** Sets a slider to `value` snapped to its step and clamped. Returns whether the setting changed. */
export function setSliderValue(m: MenuModel, item: Extract<MenuItem, { kind: 'slider' }>, settings: Settings, value: number): boolean {
  if (!Number.isFinite(value)) return false
  const next = roundToStep(value, item.range)
  if (next === settings[item.id]) return false
  settings[item.id] = next
  m.changed = item.id
  return true
}

/** Sets the aim assist select. Returns whether the setting changed. */
export function setAimAssist(m: MenuModel, settings: Settings, level: AimAssistLevel): boolean {
  if (settings.aimAssist === level) return false
  settings.aimAssist = level
  m.changed = 'aimAssist'
  return true
}

export type SelectItem = Extract<MenuItem, { kind: 'select' }>

function isOneOf<T extends string>(list: readonly T[], value: string): value is T {
  return (list as readonly string[]).includes(value)
}

/** Writes a validated select value into its setting. Returns false for a value the setting does not take. */
function writeSelect(settings: Settings, id: SelectId, value: string): boolean {
  switch (id) {
    case 'quality':
      if (!isOneOf(QUALITY_PRESETS, value)) return false
      settings.quality = value
      return true
    case 'aimAssist':
      if (!isOneOf(AIM_ASSIST_LEVELS, value)) return false
      settings.aimAssist = value
      return true
    case 'textContrast':
      if (!isOneOf(TEXT_CONTRASTS, value)) return false
      settings.textContrast = value
      return true
    case 'reducedMotion':
      if (!isOneOf(REDUCED_MOTION_MODES, value)) return false
      settings.reducedMotion = value
      return true
    case 'hudLabels':
      if (!isOneOf(HUD_LABEL_MODES, value)) return false
      settings.hudLabels = value
      return true
    case 'dashboardTime':
      if (!isOneOf(DASHBOARD_TIME_MODES, value)) return false
      settings.dashboardTime = value
      return true
  }
}

/** Sets any select to `value` (a mouse click on a segment). Returns whether the setting changed. */
export function setSelectValue(m: MenuModel, item: SelectItem, settings: Settings, value: string): boolean {
  if (selectValue(item.id, settings) === value || !item.options.includes(value)) return false
  if (!writeSelect(settings, item.id, value)) return false
  m.changed = item.id
  return true
}

/** Steps a select by `dir` (wrapping when `wrap`). Returns whether the setting changed. */
function stepSelect(m: MenuModel, item: SelectItem, settings: Settings, dir: -1 | 1, wrap: boolean): boolean {
  const n = item.options.length
  let i = item.options.indexOf(selectValue(item.id, settings)) + dir
  if (wrap) i = (i + n) % n
  else if (i < 0 || i >= n) return false
  return setSelectValue(m, item, settings, item.options[i])
}

/** Sets the quality select. Returns whether the setting changed. */
export function setQuality(m: MenuModel, settings: Settings, preset: QualityPreset): boolean {
  if (settings.quality === preset) return false
  settings.quality = preset
  m.changed = 'quality'
  return true
}

function toggle(m: MenuModel, id: ToggleId, settings: Settings): MenuAction {
  settings[id] = !settings[id]
  m.changed = id
  return 'changed'
}

function enterScreen(m: MenuModel, screen: MenuScreen): MenuAction {
  if (m.screen === 'main') m.mainFocus = m.focus
  if (m.screen === 'help' && screen === 'helpPage') m.helpFocus = m.focus
  const from = m.screen
  m.screen = screen
  if (screen === 'main') m.focus = m.mainFocus
  else if (screen === 'help' && from === 'helpPage') m.focus = m.helpFocus
  else m.focus = 0
  m.slotAction = 0
  return 'screen'
}

const OPENS: Readonly<Partial<Record<ButtonId, MenuScreen>>> = {
  openSaves: 'saves',
  openSettings: 'settings',
  openAudio: 'audio',
  openControls: 'controls',
  openHelp: 'help',
  quitTitle: 'quit',
}

function pressSlot(m: MenuModel, item: SlotItem): MenuAction {
  const a = item.actions[m.slotAction]
  if (a === undefined) return 'none'
  if (!a.enabled) {
    m.denied = a.reason
    return 'denied'
  }
  m.request = { kind: a.id, slot: item.id.slice('slot:'.length) }
  return 'request'
}

function press(m: MenuModel, item: MenuItem, settings: Settings): MenuAction {
  switch (item.kind) {
    case 'button':
      if (item.id === 'resume') return 'resume'
      if (item.id === 'openDashboard') return 'dashboard'
      if (item.id === 'quitConfirm') {
        m.request = { kind: 'quit' }
        return 'request'
      }
      if (item.id.startsWith('help:')) {
        m.helpPage = item.id.slice('help:'.length)
        return enterScreen(m, 'helpPage')
      }
      return enterScreen(m, OPENS[item.id] ?? (m.screen === 'helpPage' ? 'help' : 'main'))
    case 'slot':
      return pressSlot(m, item)
    case 'toggle':
      return toggle(m, item.id, settings)
    case 'select':
      return stepSelect(m, item, settings, 1, true) ? 'changed' : 'none'
    case 'slider':
      return 'none'
  }
}

function adjust(m: MenuModel, item: MenuItem, settings: Settings, dir: -1 | 1): MenuAction {
  switch (item.kind) {
    case 'slider':
      return setSliderValue(m, item, settings, settings[item.id] + dir * item.range.step) ? 'changed' : 'none'
    case 'toggle':
      // Left = off, right = on, like a console options list.
      if (settings[item.id] === (dir > 0)) return 'none'
      return toggle(m, item.id, settings)
    case 'select':
      return stepSelect(m, item, settings, dir, false) ? 'changed' : 'none'
    case 'slot': {
      const next = m.slotAction + dir
      if (next < 0 || next >= item.actions.length) return 'none'
      m.slotAction = next
      return 'moved'
    }
    case 'button':
      return 'none'
  }
}

/** Applies one input to an open menu. Setting changes mutate `settings` and set `m.changed`. */
export function menuInput(m: MenuModel, input: MenuInput, settings: Settings): MenuAction {
  if (!m.open || covered) return 'none'
  const items = currentItems(m)
  switch (input) {
    case 'start':
      return 'resume'
    case 'back':
      return m.screen === 'main' ? 'none' : enterScreen(m, m.screen === 'helpPage' ? 'help' : 'main')
    case 'up':
    case 'down': {
      // The controls and help page screens have one focusable item: up/down scroll the text instead.
      if (m.screen === 'controls' || m.screen === 'helpPage') return input === 'up' ? 'scrollUp' : 'scrollDown'
      const n = items.length
      m.focus = (m.focus + (input === 'up' ? n - 1 : 1)) % n
      m.slotAction = 0
      return 'moved'
    }
    case 'left':
    case 'right':
      return adjust(m, items[m.focus], settings, input === 'left' ? -1 : 1)
    case 'activate':
      return press(m, items[m.focus], settings)
  }
}

/** Display text for a setting row's value. */
export function formatItemValue(item: MenuItem, settings: Settings): string {
  switch (item.kind) {
    case 'slider':
      return item.id === 'mouseSensitivity' ? `${settings.mouseSensitivity.toFixed(2)}×` : `${Math.round(settings[item.id] * 100)}%`
    case 'toggle':
      return settings[item.id] ? 'On' : 'Off'
    case 'select':
      return selectOptionLabel(item.id, selectValue(item.id, settings))
    case 'button':
    case 'slot':
      return ''
  }
}

/** 0..1 position of a slider's value in its range (for the fill bar). */
export function sliderFraction(item: Extract<MenuItem, { kind: 'slider' }>, settings: Settings): number {
  const { min, max } = item.range
  return (settings[item.id] - min) / (max - min)
}

// ---------------------------------------------------------------------------------------------
// Gamepad navigation (edges + auto-repeat; run every frame, even while the menu is closed)
// ---------------------------------------------------------------------------------------------

/** Standard-mapping buttons the menu listens to. */
export const PAD_NAV = Object.freeze({ a: 0, b: 1, up: 12, down: 13, left: 14, right: 15 })

/** Direction slots in `PadNavState.held` (up, down, left, right): input, D-pad button, left-stick axis and sign. */
const DIR_INPUTS: readonly MenuInput[] = ['up', 'down', 'left', 'right']
const DIR_BUTTONS: readonly number[] = [PAD_NAV.up, PAD_NAV.down, PAD_NAV.left, PAD_NAV.right]
// Standard mapping: left stick Y is negative up.
const DIR_AXES: readonly number[] = [1, 1, 0, 0]
const DIR_SIGNS: readonly (1 | -1)[] = [-1, 1, -1, 1]

export interface PadNavState {
  readonly cfg: MenuNavConfig
  /** Button index for `start` (bindings' pause button). */
  readonly startButton: number
  /** Previous frame's pressed state for A, B and Start. */
  prevA: boolean
  prevB: boolean
  prevStart: boolean
  /** Per direction (up, down, left, right): held last frame (D-pad or stick, with stick hysteresis). */
  readonly held: Uint8Array
  /** Seconds until the held direction repeats. */
  readonly repeatIn: Float32Array
  /** While false, nothing fires until every nav input is released (swallows the press that opened the menu). */
  armed: boolean
}

export function createPadNav(cfg: MenuNavConfig, startButton: number): PadNavState {
  return {
    cfg,
    startButton,
    prevA: false,
    prevB: false,
    prevStart: false,
    held: new Uint8Array(4),
    repeatIn: new Float32Array(4),
    armed: true,
  }
}

/** Ignore everything until all nav buttons are up (call when the menu opens). */
export function disarmPadNav(nav: PadNavState): void {
  nav.armed = false
}

function pressed(pad: PadLike, index: number): boolean {
  return pad.buttons[index]?.pressed === true
}

function stickDir(pad: PadLike, axis: number, sign: 1 | -1, wasHeld: boolean, cfg: MenuNavConfig): boolean {
  const v = (pad.axes[axis] ?? 0) * sign
  return v > (wasHeld ? cfg.stickRelease : cfg.stickThreshold)
}

/**
 * Reads the pad once per frame and returns at most one menu input: A/B/Start on their rising edge, directions
 * on their rising edge and then auto-repeating while held. Priority: start, back, activate, directions.
 * Allocation-free (D-014).
 */
export function stepPadNav(nav: PadNavState, pad: PadLike | null, dt: number): MenuInput | null {
  const a = pad !== null && pressed(pad, PAD_NAV.a)
  const b = pad !== null && pressed(pad, PAD_NAV.b)
  const start = pad !== null && pressed(pad, nav.startButton)
  const aEdge = a && !nav.prevA
  const bEdge = b && !nav.prevB
  const startEdge = start && !nav.prevStart
  nav.prevA = a
  nav.prevB = b
  nav.prevStart = start

  let dirResult: MenuInput | null = null
  let anyDir = false
  for (let d = 0; d < 4; d++) {
    const wasHeld = nav.held[d] === 1
    const down = pad !== null && (pressed(pad, DIR_BUTTONS[d]) || stickDir(pad, DIR_AXES[d], DIR_SIGNS[d], wasHeld, nav.cfg))
    nav.held[d] = down ? 1 : 0
    if (!down) continue
    anyDir = true
    if (!wasHeld) {
      nav.repeatIn[d] = nav.cfg.repeatDelay
      if (dirResult === null) dirResult = DIR_INPUTS[d]
    } else {
      nav.repeatIn[d] -= dt
      if (nav.repeatIn[d] <= 0) {
        nav.repeatIn[d] += nav.cfg.repeatInterval
        if (nav.repeatIn[d] <= 0) nav.repeatIn[d] = nav.cfg.repeatInterval
        if (dirResult === null) dirResult = DIR_INPUTS[d]
      }
    }
  }

  if (!nav.armed) {
    if (!a && !b && !start && !anyDir) nav.armed = true
    return null
  }
  if (startEdge) return 'start'
  if (bEdge) return 'back'
  if (aEdge) return 'activate'
  return dirResult
}

/** Keyboard `code` → menu input while the menu is open (null = not a menu key). `repeat` = auto-repeat keydown. */
export function keyToMenuInput(code: string, repeat: boolean): MenuInput | null {
  switch (code) {
    case 'ArrowUp':
    case 'KeyW':
      return 'up'
    case 'ArrowDown':
    case 'KeyS':
      return 'down'
    case 'ArrowLeft':
    case 'KeyA':
      return 'left'
    case 'ArrowRight':
    case 'KeyD':
      return 'right'
    case 'Enter':
    case 'NumpadEnter':
    case 'Space':
      return repeat ? null : 'activate'
    case 'Escape':
    case 'Backspace':
      return repeat ? null : 'back'
    case 'KeyP':
      return repeat ? null : 'start'
    default:
      return null
  }
}
